import { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import Box from '@mui/material/Box';
import { useHistory, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { FormProvider, useFieldArray, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { v4 as uuidv4 } from 'uuid';
import { set } from 'lodash';
import {
  Button,
  Dialog,
  DialogTitle,
  Divider,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import {
  addMenuProduct,
  deleteMenuProduct,
  updateMenuProduct,
} from 'redux/actions/Menu/menu';
import {
  getCategoryProductsV2,
  getProductById,
  getProductsAll,
} from 'redux/actions/Menu/products';
import {
  deleteModifier,
  getModifiersAll,
  groupModifier,
  reorderModifiers,
} from 'redux/actions/Menu/modifiers';
import {
  generateProductInventory,
  getBusinessInventoryList,
} from 'redux/actions/inventory/inventory';
import { getPrinterTags } from 'redux/actions/printers';
import { StyledTab, TabsList } from './styled';
import ProductFormBasic from 'components/MenuNew/FormView/ProductFormBasic';
import ProductFormDetails from 'components/MenuNew/FormView/ProductFormDetails';
import ProductFormInventory from 'components/MenuNew/FormView/Inventory/ProductFormInventory';
import FormHeader from 'components/MenuNew/FormView/FormHeader';
import Preloader from 'components/Preloader';
import { confirm } from 'components/MenuNew/ConfirmModal';
import UpdateAvailability from './UpdateAvailability/UpdateAvailability';
import {
  SET_CHANGE_PRICE_V2,
  SET_FORM_PRODUCT,
  SET_LOADING,
} from 'redux/actions/types';
import Private from 'components/Private';
import { SOLD_OUT_STATUS } from 'constants/enumSoldOut';
import { MENU_EDIT } from 'constants/permissions';
import useQuery from 'hooks/useQuery';
import usePermission from 'hooks/permission';
import ModifierGroupForm from '../NewModifierGroup/index';
import { CONTEXT_TYPES } from 'constants/menu';
import ModifierOptions from './ModifierOption/index';
import FormFooter from './FormFooter';
import { getMenusV2 } from 'redux/actions/Menu/menuV2';
import { markMenuBulkUnsaved } from 'redux/actions/Menu/markMenuBulk';
import { priceTransform } from 'helpers/formPrice';
import ConfirmPromptModal from 'components/ConfirmPromptModal/ConfirmPromptModal';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { getNextIDsAfterConfirmPopup } from 'utils/menu';
import useProviderConfig from 'hooks/useProviderConfig';
import ConfirmProductActions from 'components/ConfirmProductActions';
import {
  ModalOpenStatusEnum,
  SET_MODAL_ACTIONS,
  SET_PRODUCT_EVENTS,
  SET_PRODUCT_EVENTS_NEXT_LOCATION,
} from 'components/ConfirmProductActions/constants';
import DefaultSelections from 'components/MenuNew/FormView/DefaultSelections';
import ProductFormStockInventory from 'components/MenuNew/FormView/Inventory/ProductFormStockInventory';
import ChangeBasePriseDialog from 'components/ChangeBasePriseDialog';
import useInventoryForm from 'components/MenuNew/hooks/useInventoryForm';
import { selectBusinessInventoryList } from 'redux/selectors';
import PromotionTab from 'pages/PosSettings/Promotions/PromotionTab';
import usePromotionsEnabled from 'hooks/usePromotionsEnabled';

/**
 * Returns true when the given product/payload resolves to a $0.00 price.
 * Handles both single-price products and multi-size products.
 * For multi-size products the backend raises the zero-price confirmation when
 * ANY size option is $0.00 (not only when every size is 0), so we must mirror
 * that here — otherwise the pending-confirmation guard misses the case and a
 * stale GET overwrites the form before the user confirms.
 */
const isZeroPriceProduct = (product) => {
  if (!product) return false;
  const sizes = product?.sizeTypeModifiers?.[0]?.list;
  if (product?.isOnePrice === false && Array.isArray(sizes) && sizes.length) {
    return sizes.some((size) => Number(size?.price) === 0);
  }
  return Number(product?.price) === 0;
};

/** Ensure each provider override has an explicit `locked` flag (default locked). */
const normalizePriceOverrideLocked = (priceOverride) => {
  if (!Array.isArray(priceOverride)) return priceOverride;
  return priceOverride.map((item) => {
    if (!item || item.context_value === 'main') return item;
    const nested = item.priceOverride
      ? normalizePriceOverrideLocked(item.priceOverride)
      : item.priceOverride;
    return {
      ...item,
      ...(nested ? { priceOverride: nested } : {}),
      locked: Object.prototype.hasOwnProperty.call(item, 'locked')
        ? item.locked
        : true,
    };
  });
};

const uniqueModifiersById = (modifiers = []) => {
  const seen = new Set();
  return modifiers.filter((modifier) => {
    if (!modifier?._id || seen.has(modifier._id)) {
      return false;
    }
    seen.add(modifier._id);
    return true;
  });
};

const getModifierSortIndex = (modifier) => {
  if (modifier?.sortIndex != null && modifier.sortIndex !== '') {
    return Number(modifier.sortIndex);
  }
  if (modifier?.sort != null && modifier.sort !== '') {
    return Number(modifier.sort);
  }
  return null;
};

const orderModifiers = (modifiers = []) => {
  const indexes = modifiers.map(getModifierSortIndex);
  if (indexes.some((index) => index == null || Number.isNaN(index))) {
    return modifiers;
  }
  return [...modifiers].sort(
    (a, b) => getModifierSortIndex(a) - getModifierSortIndex(b)
  );
};

const modifierSnapshotKey = (modifiers) =>
  uniqueModifiersById(modifiers)
    .map((item) => {
      const listKey = (item.list || [])
        .map((opt) => {
          const prices = (opt.priceOverride || [])
            .map(
              (price) =>
                `${price.context_type || ''}:${price.context_value || ''}:${
                  price.price ?? ''
                }`
            )
            .join(',');
          return `${opt._id || ''}:${opt.name || ''}:${opt.price ?? ''}:${prices}`;
        })
        .join('|');
      return [
        item._id,
        item.name || '',
        item.title || '',
        item.min ?? '',
        item.max ?? '',
        item.free ?? '',
        item.isOnePrice ?? '',
        listKey,
      ].join('/');
    })
    .join(';');

/** Opening/hydrating modifiers dirties RHF without real user edits. */
const hasMeaningfulProductEdits = ({
  isDirty,
  dirtyFields,
  values,
  savedProduct,
  imageGetValue,
}) => {
  const modifiersChanged =
    modifierSnapshotKey(values?.modifiers) !==
    modifierSnapshotKey(savedProduct?.modifiers);
  const dirtyKeys = Object.keys(dirtyFields || {}).filter((key) => {
    if (key === 'soldOut') {
      return false;
    }
    if (key === 'modifiers') {
      return modifiersChanged;
    }
    if (key === 'image') {
      return getImageUrl(values?.image) !== getImageUrl(savedProduct?.image);
    }
    return true;
  });
  const imageChanged =
    Boolean(imageGetValue) &&
    getImageUrl(values?.image) !== getImageUrl(savedProduct?.image);
  return (
    (Boolean(isDirty) && dirtyKeys.length > 0) ||
    imageChanged ||
    modifiersChanged
  );
};

/** Normalize API product shape so reset defaults match rendered form values. */
const normalizeProductFormValues = (product) => {
  if (!product) return product;
  const sizeTypeModifiers = Array.isArray(product.sizeTypeModifiers)
    ? product.sizeTypeModifiers.map((modifier) => ({
        ...modifier,
        list: Array.isArray(modifier?.list)
          ? modifier.list.map((size) => ({
              ...size,
              priceOverride: normalizePriceOverrideLocked(size?.priceOverride),
            }))
          : modifier?.list,
      }))
    : product.sizeTypeModifiers;
  return {
    ...product,
    price: product.price === '' || product.price == null ? 0 : product.price,
    ingredients: (product.ingredients || []).map((item) =>
      item?.name ? item.name : item
    ),
    image: {
      ...(product.image || {}),
      url: product.image?.url || '',
      imageId: product.image?.imageId || product.image?.fileId || '',
    },
    priceOverride: normalizePriceOverrideLocked(product.priceOverride),
    sizeTypeModifiers,
    modifiers: orderModifiers(uniqueModifiersById(product.modifiers)),
  };
};

/** Product PUT/GET often omits `modifiers`. Keep the last known list so a
 * later assign does not persist only the newly added group. */
const withPreservedModifiers = (product, fallbackModifiers) => {
  if (!product) return product;
  const fromProduct = uniqueModifiersById(product.modifiers);
  if (fromProduct.length) {
    return { ...product, modifiers: fromProduct };
  }
  return {
    ...product,
    modifiers: uniqueModifiersById(fallbackModifiers),
  };
};

const getImageUrl = (image) => image?.url || '';

function BasePriceConfirmDialog(props) {
  const { t } = useTranslation();

  const { onClose, setConfirmed, open } = props;

  const handleClose = () => {
    onClose();
  };

  return (
    <Dialog onClose={handleClose} open={open}>
      <DialogTitle>Hello</DialogTitle>
      <Button onClick={() => setConfirmed(true)}>Yes</Button>
      <Button onClick={() => setConfirmed(false)}>{t('orders.cancel')}</Button>
    </Dialog>
  );
}

function TabPanel(props) {
  const { value, index, content, style } = props;

  return (
    <div role="tabpanel" style={style} hidden={value !== index}>
      {value === index && <Box sx={{ py: 3 }}>{content}</Box>}
    </div>
  );
}

const initialData = {
  name: '',
  price: '',
  refs: [],
  websitePrice: '',
  image: {
    url: '',
    imageId: '',
  },
  description: '',
  carbs: '',
  sort: 0,
  tax: '',
  calories: '',
  proteins: '',
  priceOverride: [
    {
      context_type: CONTEXT_TYPES.provider,
      context_value: 'main',
      price: 0,
      pickupPrice: '',
      locked: true,
    },
  ],
  modifiers: [],
  isActive: true,
  onlyUsedAsModifer: false,
  sizeTypeModifiers: [],
  isOnePrice: true,
  connectedProviders: { main: true },
  category: '',
  classifications: [],
  ingredients: [],
  soldOut: {
    status: SOLD_OUT_STATUS.AVAILABILITY,
    soldOutDate: '',
  },
  onItsOwn: true,
  isPopular: false,
  availableInKiosk: false,
  enforceQtyStrict: false,
  isWeighted: null,

  popularStartDate: new Date().toISOString(),
  popularEndDate: new Date().toISOString(),
  isPermanentlyPopular: false,
  preselectOptions: {
    defaultSelection: false,
    options: {},
  },
};

const initialModifierData = {
  min: '',
  max: '',
  free: '',
  name: '',
  title: '',
  description: '',
  list: [
    {
      name: '',
      price: 0,
      priceOverride: [],
      optionSort: 0,
      pickupPrice: null,
      locked: true,
    },
  ],
  isActive: true,
  internalName: '',
  isOnePrice: true,
};

const productFormInfo = (
  providers,
  t = () => {},
  isEdit,
  useDisablePriceLimitCheck
) => {
  return yup.object().shape({
    name: yup.string().required('field_is_required_'),
    isPopular: yup.boolean().nullable(),
    availableInKiosk: yup.boolean().nullable(),
    isWeighted: yup.boolean().nullable(),
    price: yup.lazy((value) =>
      value === ''
        ? yup.string().required('field_is_required_')
        : yup
            .number()
            .min(0, 'price_must_equal_to_0_')
            .transform((value) => (isNaN(value) ? null : value))
            .typeError('field_is_required_')
            .required('field_is_required_')
            .test(
              'sizeTypeModifiers',
              'price_$375.00_for_ubereats_',
              (price, option) => {
                if (useDisablePriceLimitCheck) return true;
                // When connected to Uber and single price, main price cannot exceed $375 regardless of overrides
                if (
                  option.parent?.connectedProviders?.[providers.ubereats.srv] &&
                  option.parent.isOnePrice &&
                  price > 374
                ) {
                  return false;
                }
                let tested = true;
                if (option.parent.priceOverride) {
                  option.parent.priceOverride.map((item) => {
                    if (item.context_value === providers.ubereats.srv) {
                      if (option.parent.isOnePrice) {
                        if (
                          option.parent?.connectedProviders[
                            providers.ubereats.srv
                          ] &&
                          price > 374 &&
                          !item.price
                        ) {
                          tested = false;
                        } else {
                          tested = true;
                        }
                      }
                    }
                  });
                  return tested;
                }
                return tested;
              }
            )
    ),
    tax: yup
      .number()
      .min(null, 'price_must_equal_to_0_')
      .transform((value) => (isNaN(value) ? null : value))
      .nullable(),
    description: yup.string().nullable(),
    priceOverride: yup.array(
      yup.object().shape({
        price: yup.lazy((value) =>
          value === ''
            ? yup.string().required('field_is_required_')
            : yup
                .number()
                .min(0, 'price_must_equal_to_0_')
                .transform((value) => (isNaN(value) ? null : value))
                .typeError('field_is_required_')
                .required('field_is_required_')
                .test(
                  'sizeTypeModifiers',
                  'price_$375.00_for_ubereats_',
                  (price, option) => {
                    if (useDisablePriceLimitCheck) return true;
                    const params = new URLSearchParams(
                      document.location.search
                    );
                    const pid = params.get('pid');
                    const isSizeType = option.from
                      .filter((item) => item.value?.isOnePrice)
                      ?.find((item) => item);
                    const connectedProviders = option.from
                      .map((item) => item.value)
                      .find((item) => item._id === pid)?.connectedProviders;
                    if (!connectedProviders) return true;
                    if (
                      !isSizeType?.value.isOnePrice &&
                      option.parent.context_value === providers.ubereats.srv &&
                      connectedProviders[providers.ubereats.srv]
                    ) {
                      return true;
                    } else {
                      return !(
                        option.parent.context_value ===
                          providers.ubereats.srv &&
                        connectedProviders[providers.ubereats.srv] &&
                        price > 374
                      );
                    }
                  }
                )
        ),
        pickupPrice: yup.lazy((value) => {
          return yup
            .number()
            .min(0, 'price_must_equal_to_0_')
            .transform((value) => (isNaN(value) ? null : value))
            .nullable()
            .test(
              'sizeTypeModifiers',
              'price_$375.00_for_ubereats_',
              (pickupPrice, option) => {
                if (useDisablePriceLimitCheck) return true;
                const params = new URLSearchParams(document.location.search);
                const pid = params.get('pid');
                const isSizeType = option.from
                  .filter((item) => item.value?.isOnePrice)
                  ?.find((item) => item);
                const connectedProviders = option.from
                  .map((item) => item.value)
                  .find((item) => item._id === pid)?.connectedProviders;
                if (!connectedProviders) return true;
                if (
                  !isSizeType?.value.isOnePrice &&
                  option.parent.context_value === providers.ubereats.srv &&
                  connectedProviders[providers.ubereats.srv]
                ) {
                  return true;
                } else {
                  return option.parent.context_value ===
                    providers.ubereats.srv &&
                    connectedProviders[providers.ubereats.srv] &&
                    pickupPrice > 374
                    ? false
                    : true;
                }
              }
            );
        }),
      })
    ),
    sizeTypeModifiers: yup.array().when('isOnePrice', {
      // Only validate size options for multi-size products. When the product is
      // single-price (`isOnePrice` true/undefined), any leftover/partial
      // `sizeTypeModifiers` data must NOT block Update.
      is: (isOnePrice) => isOnePrice === false,
      then: yup.array(
        yup.object().shape({
          name: yup.string().required('field_is_required_'),
          list: yup.array(
            yup.object().shape({
              name: yup.string().required('field_is_required_'),
              optionSort: yup.number().required('field_is_required_'),
              price: yup.lazy((value) =>
                value === ''
                  ? yup.string().required('field_is_required_')
                  : yup
                      .number()
                      .min(0, 'price_must_equal_to_0_')
                      .transform((value) => (isNaN(value) ? null : value))
                      .typeError('field_is_required_')
                      .required('field_is_required_')
                      .test(
                        'list',
                        'price_$375.00_for_ubereats_',
                        (price, option) => {
                          if (useDisablePriceLimitCheck) return true;
                          const params = new URLSearchParams(
                            document.location.search
                          );
                          const pid = params.get('pid');
                          if (
                            option.from &&
                            value &&
                            typeof value === 'number'
                          ) {
                            const connectedProviders = option.from
                              .map((item) => item.value)
                              .find(
                                (item) => item._id === pid
                              )?.connectedProviders;

                            if (!connectedProviders) return true;

                            let tested = true;
                            if (option.parent.priceOverride) {
                              option.parent.priceOverride.map((item) => {
                                if (
                                  item.context_value === providers.ubereats.srv
                                ) {
                                  if (
                                    connectedProviders[
                                      providers.ubereats.srv
                                    ] &&
                                    price > 374 &&
                                    !item.price
                                  ) {
                                    tested = false;
                                  } else {
                                    tested = true;
                                  }
                                }
                              });
                              return tested;
                            }
                          } else {
                            return true;
                          }
                        }
                      )
              ),
              priceOverride: yup.array(
                yup.object().shape({
                  price: yup.lazy((value) =>
                    yup
                      .number()
                      .transform((value) => (isNaN(value) ? null : value))
                      .nullable()
                      .test(
                        'sizeTypeModifiers',
                        'price_$375.00_for_ubereats_',
                        (price, option) => {
                          if (useDisablePriceLimitCheck) return true;
                          const params = new URLSearchParams(
                            document.location.search
                          );
                          const pid = params.get('pid');
                          if (
                            option.from &&
                            value &&
                            typeof value === 'number' &&
                            option.parent.context_value ===
                              providers.ubereats.srv
                          ) {
                            const connectedProviders = option?.from
                              ?.map((item) => item.value)
                              ?.find(
                                (item) => item._id === pid
                              )?.connectedProviders;

                            if (!connectedProviders) return true;

                            return !(
                              connectedProviders[providers.ubereats.srv] &&
                              option.parent.context_value ===
                                providers.ubereats.srv &&
                              price > 374
                            );
                          } else {
                            return true;
                          }
                        }
                      )
                  ),
                  pickupPrice: yup.lazy((value, option) => {
                    return yup
                      .number()
                      .transform((value) => (isNaN(value) ? null : value))
                      .nullable()
                      .test(
                        'sizeTypeModifiers',
                        'price_$375.00_for_ubereats_',
                        (pickupPrice, option) => {
                          if (useDisablePriceLimitCheck) return true;
                          const params = new URLSearchParams(
                            document.location.search
                          );
                          const pid = params.get('pid');
                          if (
                            option.from &&
                            value &&
                            typeof value === 'number' &&
                            option.parent.context_value ===
                              providers.ubereats.srv
                          ) {
                            const connectedProviders = option?.from
                              ?.map((item) => item.value)
                              ?.find(
                                (item) => item._id === pid
                              )?.connectedProviders;

                            if (!connectedProviders) return true;

                            return connectedProviders[providers.ubereats.srv] &&
                              option.parent.context_value ===
                                providers.ubereats.srv &&
                              pickupPrice > 374
                              ? false
                              : true;
                          } else {
                            return true;
                          }
                        }
                      );
                  }),
                })
              ),
            })
          ),
        })
      ),
      otherwise: yup.array().nullable(),
    }),
    // other fields remain unchanged...
  });
};

const ProductForm = () => {
  const { t } = useTranslation();
  const { providers } = useProviderConfig();
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const query = useQuery();
  const mode = query.get('mode');
  const isEdit = mode === 'edit';
  const searchParams = new URLSearchParams(location.search);
  const checkPermission = usePermission();
  const promotionsEnabled = usePromotionsEnabled();
  const isInOverview = history.location.pathname.includes('overview');
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(false);
  const isSavingModifierGroupRef = useRef(false); // Track if handleSaveModifierGroup is executing to prevent duplicate calls
  const [loadingDuplicate, setLoadingDuplicate] = useState(false);
  const [basePriceConfirmDialogOpen, handleBasePriceConfirmDialogOpen] =
    useState(false);
  const [currentProduct, setCurrentProduct] = useState(null);
  const [productIsModOption, handleProductIsModOption] = useState(false);
  const [currentCategory, setCurrentCategory] = useState(null);
  const [currentModifier, setCurrentModifier] = useState(null);
  const [hasDeletedItem, setHasDeletedItem] = useState(false);
  const [currentModifierName, setCurrentModifierName] = useState(
    currentModifier?.name
  );
  const { currentBarcodeNumber } = useSelector(({ inventory }) => inventory);
  const { menu, formProduct, changeBasePriceData, menuAvailable } = useSelector(
    ({ menu }) => menu
  );
  const userData = useSelector(({ user: { data } }) => ({
    data,
  }));
  dayjs.extend(utc);
  dayjs.extend(timezone);
  const { currentBusiness } = useSelector(({ business }) => business);
  // const [prompt, setDirty, setPristine] = useModifyForm();
  const [isDirty, setIsDirty] = useState(false);
  const [modifierFormState, setModifierFormState] =
    useState(initialModifierData);
  const { productEvents } = useSelector((store) => store);

  const readOnly = useMemo(() => {
    return !checkPermission(MENU_EDIT);
  }, [checkPermission, menu]);

  const [isPriceDirty, setIsPriceDirty] = useState(false);

  const [generateInventoryLoading, setGenerateInventoryLoading] =
    useState(false);

  // const priceWatcher = useWatch('price');
  const productFormMethods = useForm({
    defaultValues: initialData,
    mode: 'all',
    resolver: yupResolver(
      productFormInfo(providers, t, isEdit, menu?.disablePriceLimitCheck)
    ),
  });

  const { reset, getValues, formState, setValue, control, trigger, register } =
    productFormMethods;
  const businessInventoryList = useSelector(selectBusinessInventoryList);

  const sizeTypeModifierMethods = useFieldArray({
    control, // control props comes from useForm (optional: if you are using FormContext)
    name: 'sizeTypeModifiers', // unique name for your Field Array
  });

  const productPriceOverride = useFieldArray({
    control, // control props comes from useForm (optional: if you are using FormContext)
    name: 'priceOverride', // unique name for your Field Array
  });

  const sizeTypeModifierListMethods = useFieldArray({
    control, // control props comes from useForm (optional: if you are using FormContext)
    name: 'sizeTypeModifiers[0].list', // unique name for your Field Array
  });

  const deletedOptionIds = useFieldArray({
    control,
    name: 'sizeTypeModifiers[0].deletedOptionIds',
  });

  const modifierGroupMethods = useFieldArray({
    control, // control props comes from useForm (optional: if you are using FormContext)
    name: 'modifiers', // unique name for your Field Array
  });

  const [title, setTitle] = useState(getValues().name);
  // const [sizeTypeModifier, handleSizeTypeModifier] = useState(getValues().sizeTypeModifier);

  const productId = query.get('pid');
  const cid = query.get('cid');
  const modifierId = query.get('mid');
  const entityType = query.get('entityType');
  const showModifier = modifierId && entityType === 'modifier';
  const isConnectedToPos = !!formProduct?.connectedProviders?.pos;
  const showPromotionTab = isEdit && isConnectedToPos && promotionsEnabled;
  const [isOpen, setIsOpen] = useState(false);
  /** Last full create payload (when `name` was present) — confirm step merges this so POST is not only { checkZeroPrice: false }. */
  const lastCreateProductPayloadRef = useRef(null);
  /**
   * Last *persisted* product, captured on load and after a successful save.
   * Used to restore form state when the user cancels a zero-price confirmation.
   * Unlike `currentProduct`, it is not mutated by optimistic price-dialog edits.
   */
  const persistedProductRef = useRef(null);
  /**
   * True while a zero-price confirmation opened by *this* form is awaiting the
   * user's decision. Lets us revert to the persisted product on Cancel without
   * reacting to unrelated product-event modals.
   */
  const pendingZeroPriceRef = useRef(false);
  /**
   * Set when Confirm re-submits with `checkZeroPrice: false`. Modifier-group
   * save must strip `productZeroPrice` in that path so the modal does not
   * reopen after the user already confirmed.
   */
  const suppressZeroPriceConfirmRef = useRef(false);
  /**
   * Groups the API confirmed as assigned to this product (product GET, or the
   * list a `/modifier/group` POST just persisted). The product PUT can answer
   * without `modifiers`, so the form alone is not a safe source of truth.
   */
  const assignedModifiersRef = useRef([]);
  /** Groups the user unassigned in this session (X on a card, group deleted). */
  const removedModifierIdsRef = useRef(new Set());
  const [getImageValue, setImageGetvalue] = useState('');
  const providerPriceOverride = [];
  const [disable, setDisable] = useState(true);
  const [formErrors, updateFormErrors] = useState({
    name: '',
    description: '',
    list: [
      {
        name: '',
        price: '',
        priceOverride: [{ name: '', price: null, pickupPrice: null }],
      },
    ],
  });
  const [isChanged, setIsChanged] = useState(false);
  const [errorData, setErrorData] = useState();
  const { inventoryFormMethods } = useInventoryForm(productFormMethods);

  const handleClickOpen = () => {
    setIsOpen(!isOpen);
  };

  const handleCloseBasePriceConfirmModal = (value) => {
    handleBasePriceConfirmDialogOpen(value);
  };

  const getProduct = () => {
    setIsPriceDirty(false);
    pendingZeroPriceRef.current = false;
    suppressZeroPriceConfirmRef.current = false;
    // Another product: its assignments must not leak into this one.
    assignedModifiersRef.current = [];
    removedModifierIdsRef.current = new Set();
    setImageGetvalue('');
    if (mode === 'edit') {
      // Full clean reset while loading — partial `{ image }` reset left the form
      // in a mismatched default/value state that looked like unsaved edits.
      reset(initialData, { keepDirtyValues: false, keepDirty: false });
      setCurrentProduct(null);
      setLoading(true);
      dispatch(getProductById(productId, cid))
        .then((res) => {
          if (res.category) {
            query.set('cid', res.category);
          } else {
            query.delete(cid);
          }
          if (!isInOverview) {
            history.push({
              pathname: history.location.pathname,
              search: query.toString(),
            });
          }
          const normalized = normalizeProductFormValues(res);
          dispatch({ type: SET_FORM_PRODUCT, payload: normalized });
          // Stale cancel/opened modal flags from a prior visit must not re-dirty
          // a freshly loaded product (would falsely show discard on tab change).
          if (!productEvents?.eventData?.length) {
            dispatch({
              type: SET_MODAL_ACTIONS,
              payload: ModalOpenStatusEnum.closed,
            });
          }
          reset(normalized, { keepDirtyValues: false, keepDirty: false });
          setCurrentProduct(normalized);
          persistedProductRef.current = normalized;
          assignedModifiersRef.current = normalized.modifiers || [];
          removedModifierIdsRef.current = new Set();
        })
        .finally(() => {
          setTabValue(0);
          setLoading(false);
        });
    } else {
      lastCreateProductPayloadRef.current = null;
      assignedModifiersRef.current = [];
      removedModifierIdsRef.current = new Set();
      sizeTypeModifierListMethods.replace([]);
      sizeTypeModifierMethods.replace([]);
      deletedOptionIds.replace([]);
      setCurrentProduct(null);
      persistedProductRef.current = null;
      reset(initialData);
    }
  };

  const handleGenerateInventory = useCallback(async () => {
    const pid = query.get('pid');
    if (!pid || !currentBusiness?.uuid) return;
    setGenerateInventoryLoading(true);
    try {
      await dispatch(generateProductInventory(pid, currentBusiness.uuid));
      const productData = await dispatch(getProductById(pid, cid));
      if (productData) {
        dispatch({ type: SET_FORM_PRODUCT, payload: productData });
        reset(productData, { keepDirtyValues: false, keepDirty: false });
        persistedProductRef.current = productData;
      }
      await dispatch(getBusinessInventoryList(currentBusiness.uuid));
    } finally {
      setGenerateInventoryLoading(false);
    }
  }, [query, currentBusiness?.uuid, cid, dispatch, reset]);

  useEffect(() => {
    getProduct();
  }, [productId]);

  useEffect(() => {
    dispatch(getPrinterTags());
  }, []);

  useEffect(() => {
    if (history.location.state?.tabValue) {
      setTabValue(history.location.state?.tabValue);
    }
  }, [history.location]);

  useEffect(() => {
    // Sync when Redux formProduct id changes. Do not depend on `!currentProduct`
    // (that re-fired reset after load and could reintroduce dirty drift).
    if (!isEdit || !formProduct?._id) return;
    if (currentProduct?._id === formProduct._id) return;
    const normalized = normalizeProductFormValues(formProduct);
    reset(normalized, { keepDirtyValues: false, keepDirty: false });
    setCurrentProduct(normalized);
  }, [formProduct?._id]);

  useEffect(() => {
    // When the user cancels a zero-price confirmation that this form opened,
    // discard the pending edits and restore the last persisted (old) product.
    if (
      productEvents.modalStatus === ModalOpenStatusEnum.cancel &&
      pendingZeroPriceRef.current
    ) {
      pendingZeroPriceRef.current = false;
      if (isEdit && persistedProductRef.current) {
        reset(persistedProductRef.current, {
          keepDirtyValues: false,
          keepDirty: false,
        });
        setCurrentProduct(persistedProductRef.current);
      }
    }
  }, [productEvents.modalStatus]);

  // useEffect(() => {
  //   if (!getValues().soldOut && mode === 'edit') {
  //     reset(currentProduct);
  //   }
  // }, [availableValue]);

  useEffect(() => {
    history.push({
      pathname: history.location.pathname,
      search: history.location.search,
      state: {},
    });
  }, []);

  useEffect(() => {
    const categoryData = menu?.categories?.find((item) => item?._id === cid);
    if (isInOverview && categoryData) {
      Object.keys(categoryData.connectedProviders).map((modItem) => {
        if (categoryData.connectedProviders[modItem]) {
          const findPrice = getValues()?.priceOverride?.find(
            (pr) => pr?.context_value === modItem
          )?.price;
          const findPickupPrice = getValues()?.priceOverride?.find(
            (pr) => pr?.context_value === modItem
          )?.pickupPrice;
          const findLocked = getValues()?.priceOverride?.find(
            (pr) => pr?.context_value === modItem
          );
          if (!isEdit) {
            providerPriceOverride.push({
              context_type: CONTEXT_TYPES.provider,
              context_value: modItem,
              name: modItem,
              price: findPrice && findPrice !== 0 ? findPrice : 0,
              pickupPrice:
                findPickupPrice && findPickupPrice !== 0 ? findPickupPrice : 0,
              locked: Object.prototype.hasOwnProperty.call(
                findLocked || {},
                'locked'
              )
                ? findLocked.locked
                : true,
            });
            setValue('priceOverride', providerPriceOverride, {
              shouldDirty: false,
              keepDefaultValues: false,
            });
          }
        }
      });
      // productPriceOverride.replace(providerPriceOverride);
      setCurrentCategory(categoryData);
      if (!modifierId) {
        if (currentProduct && mode !== 'create') {
          if (
            getValues()._id === productId ||
            currentProduct?._id === productId
          ) {
            // Only preserve dirty/defaults while a confirmation opened by this
            // form is actually pending. Stale cancel/opened status alone must
            // not mark an untouched product dirty.
            const keepDirtyForPendingModal =
              pendingZeroPriceRef.current &&
              (productEvents.modalStatus === ModalOpenStatusEnum.cancel ||
                productEvents.modalStatus === ModalOpenStatusEnum.opened);
            reset(getValues()?._id ? getValues() : currentProduct, {
              keepDirtyValues: keepDirtyForPendingModal,
              keepDirty: keepDirtyForPendingModal,
              keepDefaultValues:
                keepDirtyForPendingModal ||
                (productEvents.modalStatus !== ModalOpenStatusEnum.confirm &&
                  history.location.state?.create) ||
                (!productEvents.eventData?.length &&
                  history.location.state?.create),
            });
          }
        } else {
          if (isInOverview) {
            reset(
              {
                ...initialData,
                category: cid,
                priceOverride: providerPriceOverride,
              },
              { keepDirtyValues: false, keepDirty: false }
            );
          } else {
            reset(
              { ...initialData, priceOverride: providerPriceOverride },
              { keepDirtyValues: false, keepDirty: false }
            );
          }
        }
      }
    }
  }, [
    mode,
    menu.categories,
    menu.productsAll,
    menu.connectedProviders,
    menu.bulkInfo,
    productId,
    cid,
  ]);

  useEffect(() => {
    setHasDeletedItem(false);
    if (mode !== 'edit' && !modifierId) {
      return setTabValue(0);
    }
    return setTabValue(tabValue);
  }, [productId, mode, cid, tabValue]);

  useEffect(() => {
    if (currentProduct?.isOnePrice) {
      dispatch(getModifiersAll(currentProduct?.isOnePrice));
    }
  }, [currentProduct?.isOnePrice]);

  useEffect(() => {
    if (sizeTypeModifierListMethods.fields.length === 1 && hasDeletedItem) {
      reset(
        {
          ...getValues(),
          isOnePrice: true,
          price: getValues('price') || '',
          pickupPrice: getValues('pickupPrice') || null,
          locked: getValues('locked') || true,
          sizeTypeModifiers: [],
        },
        { keepDirtyValues: true, keepDirty: true }
      );
    } else {
      const price = getValues('price');
      // Normalize empty price without marking the form dirty on load/sync.
      if (price === '' || price == null) {
        setValue('price', 0, { shouldDirty: false, shouldTouch: false });
      }
    }
  }, [sizeTypeModifierListMethods.fields]);

  useEffect(() => {
    const defaultBusiness = userData.data?.business;
    if (
      defaultBusiness?._id !== currentBusiness?._id &&
      Object.keys(formState.touchedFields).length
    ) {
      handleSaveData();
    }
  }, [currentBusiness]);

  const routerGoBack = (cid) => {
    if (isInOverview) {
      history.push('/menu/overview');
    } else {
      history.push('/menu/items');
    }
    // // Example: Change the query params to ?name=JohnDoe
    // const newSearchParams = new URLSearchParams();
    // newSearchParams.set('cid', cid);
    // history.push({
    //   search: newSearchParams.toString(),
    // });
  };

  const handleValidSubmit = useCallback(
    async (values, e, change = false, newLocation = null) => {
      if (tabValue === 4 || (tabValue === 5 && isConnectedToPos)) {
        return;
      }
      if (
        Object.prototype.hasOwnProperty.call(values || {}, 'barcodeNumbers')
      ) {
        values.barcodeNumbers = currentBarcodeNumber;
      }

      if (
        formProduct.modifiers &&
        JSON.stringify(values.modifiers) !==
          JSON.stringify(formProduct.modifiers)
      ) {
        const newData = getNextIDsAfterConfirmPopup(newLocation);
        if (newData?.newProductId) {
          handleSaveModifierGroup(history.location);
          dispatch(
            getProductById(newData?.newProductId, newData?.newCatId)
          ).then((res) => {
            dispatch({ type: SET_FORM_PRODUCT, payload: res });
            reset(res);
          });
        }
      }
      if (e) {
        e.preventDefault();
      }

      const isError =
        Object.keys(productFormMethods.formState.errors)?.length || errorData
          ? Object.keys(errorData)?.length
          : !!errorData;

      if (isError) {
        Object.keys(errorData).forEach((key) => {
          productFormMethods.setError(key, { message: errorData[key].message });
        });
        return;
      }
      if (errorData && !Object.keys(errorData)?.length) {
        Object.keys(errorData).forEach((key) => {
          productFormMethods.clearErrors(key);
        });
      }

      if (values.popularStartDate || values.popularEndDate) {
        values.popularStartDate = dayjs(values.popularStartDate)
          .tz(currentBusiness.timezone?.timeZoneId, false)
          .set('date', dayjs(values.popularStartDate).get('date'))
          .set('month', dayjs(values.popularStartDate).get('month'))
          .set('year', dayjs(values.popularStartDate).get('year'))
          .startOf('day')
          .utc();
        // .format('YYYY-MM-DDTHH:mm:ss');
        values.popularEndDate = dayjs(values.popularEndDate)
          .tz(currentBusiness.timezone?.timeZoneId, false)
          .set('date', dayjs(values.popularEndDate).get('date'))
          .set('month', dayjs(values.popularEndDate).get('month'))
          .set('year', dayjs(values.popularEndDate).get('year'))
          .endOf('day')
          .utc();
        // .utc();
        // .format('YYYY-MM-DDTHH:mm:ss');
      }
      // handleSaveModifierGroup();
      if (values?.isOnePrice) {
        // Single-price product: it must not carry any size options. Leftover
        // `sizeTypeModifiers` from a removed multi-size setup (often containing
        // `null` / partial `{ optionSort }` entries) would be rejected by the
        // API, so send a clean empty array.
        values.sizeTypeModifiers = [];
      } else if (values?.sizeTypeModifiers?.[0]) {
        // Multi-size: drop any null entries left behind by removed options
        // before re-indexing, so we never send `null` items or crash on them.
        if (Array.isArray(values.sizeTypeModifiers[0].list)) {
          values.sizeTypeModifiers[0].list =
            values.sizeTypeModifiers[0].list.filter((item) => item != null);
        }
        values.sizeTypeModifiers[0].list?.forEach(
          (item, index) => (item.optionSort = index)
        );
      }

      values.ingredients = values.ingredients?.map((item) => {
        if (!item?._id) {
          return { name: item };
        } else {
          return { name: item?.name };
        }
      });

      // Persist explicit lock flags so only unlocked providers stay open after save/reload.
      values.priceOverride = normalizePriceOverrideLocked(values.priceOverride);
      if (Array.isArray(values.sizeTypeModifiers)) {
        values.sizeTypeModifiers = values.sizeTypeModifiers.map((modifier) => ({
          ...modifier,
          list: Array.isArray(modifier?.list)
            ? modifier.list.map((size) => ({
                ...size,
                priceOverride: normalizePriceOverrideLocked(
                  size?.priceOverride
                ),
              }))
            : modifier?.list,
        }));
      }

      //Setting SortOption value as ordered in array
      if (isEdit) {
        dispatch({ type: SET_LOADING, payload: true });
        setLoading(true);
        // Confirm re-submit: modifier save must not reopen the zero-price modal.
        suppressZeroPriceConfirmRef.current = values.checkZeroPrice === false;
        // Arm before parallel modifier/product requests so a category refresh
        // cannot wipe provider price edits while zero-price confirm is pending.
        if (isZeroPriceProduct(values)) {
          pendingZeroPriceRef.current = true;
        }
        handleSaveModifierGroup(history.location);
        // Keep the groups the API has assigned in the payload: the form may
        // have been reset without them by an earlier update.
        values.modifiers = withKnownAssignedModifiers(values.modifiers);

        return dispatch(
          updateMenuProduct(
            productId,
            { category: values.category || currentCategory?._id, ...values },
            cid
          )
        )
          .then((res) => {
            // Update or add a parameter
            if (cid !== values.category) {
              searchParams.set('cid', values.category);
              const newQueryString = '?' + searchParams.toString();
              history.replace({
                pathname: location.pathname,
                search: newQueryString,
              });
            }

            if (
              res.data?._id &&
              !res.events?.length &&
              (productEvents.modalStatus !== ModalOpenStatusEnum.cancel ||
                productEvents.modalStatus !== ModalOpenStatusEnum.opened)
            ) {
              dispatch({
                type: SET_MODAL_ACTIONS,
                payload: ModalOpenStatusEnum.closed,
              });
              // Save committed: no confirmation is pending anymore.
              pendingZeroPriceRef.current = false;
              const updatedProduct = withPreservedModifiers(
                res?.data,
                values?.modifiers ||
                  persistedProductRef.current?.modifiers ||
                  currentProduct?.modifiers
              );
              // The PUT can answer with the pre-assignment list while the
              // parallel `/modifier/group` save is still in flight.
              const savedProduct = normalizeProductFormValues({
                ...updatedProduct,
                modifiers: withKnownAssignedModifiers(
                  updatedProduct?.modifiers
                ),
              });
              dispatch({ type: SET_FORM_PRODUCT, payload: savedProduct });
              setCurrentProduct(savedProduct);
              persistedProductRef.current = savedProduct;
              reset(savedProduct, { keepDirtyValues: false, keepDirty: false });
            }

            if (res.events?.length) {
              localStorage.setItem(
                'SET_MODAL_ACTIONS',
                ModalOpenStatusEnum.opened
              );
              // A zero-price confirmation is now awaiting the user's decision.
              pendingZeroPriceRef.current = res.events.some(
                (event) => event?.tag === 'productZeroPrice'
              );
              setLoading(false);
              dispatch({ type: SET_LOADING, payload: false });
            }

            if (cid !== values.category && isInOverview) {
              routerGoBack(res?.data.category);
            }
          })
          .finally(() => {
            setImageGetvalue('');
            setLoading(false);
            dispatch({
              type: SET_CHANGE_PRICE_V2,
              payload: {
                changeBasePrice: false,
                title: '',
                msg: '',
                msg2: '',
                price: 0,
                name: '',
                oldPrice: 0,
                isConfirm: false,
              },
            });
            dispatch({ type: SET_LOADING, payload: false });
          });
      } else {
        dispatch({ type: SET_LOADING, payload: true });
        setLoading(true);

        const saved = lastCreateProductPayloadRef.current || {};
        let payload = { ...saved, ...values };

        if (saved.name?.trim?.() && !String(payload.name || '').trim()) {
          payload.name = saved.name;
        }

        const savedListLen = saved.sizeTypeModifiers?.[0]?.list?.length ?? 0;
        const payloadListLen =
          payload.sizeTypeModifiers?.[0]?.list?.length ?? 0;
        const sizesLost =
          savedListLen > 0 &&
          (payloadListLen === 0 || payloadListLen < savedListLen);
        const multiSizeModeLost =
          saved.isOnePrice === false &&
          savedListLen > 0 &&
          payload.isOnePrice === true;

        if (sizesLost || multiSizeModeLost) {
          payload.sizeTypeModifiers = JSON.parse(
            JSON.stringify(saved.sizeTypeModifiers)
          );
          payload.isOnePrice = saved.isOnePrice;
        }

        if (values?.name?.trim?.()) {
          lastCreateProductPayloadRef.current = JSON.parse(
            JSON.stringify(payload)
          );
        }

        return dispatch(addMenuProduct(payload))
          .then((res) => {
            if (
              res?.data ||
              Object.prototype.hasOwnProperty.call(
                payload || {},
                'checkZeroPrice'
              ) ||
              (!res?.data && res?._id)
            ) {
              history.push({ pathname: `/menu/overview`, state: 'skip' });
              dispatch({
                type: SET_MODAL_ACTIONS,
                payload: ModalOpenStatusEnum.closed,
              });
              setCurrentProduct(res);
              reset(res);
            } else {
              dispatch({
                type: SET_PRODUCT_EVENTS_NEXT_LOCATION,
                payload: `/menu/overview`,
              });
            }
          })
          .finally(() => {
            setImageGetvalue('');
            setLoading(false);
            dispatch({
              type: SET_CHANGE_PRICE_V2,
              payload: {
                changeBasePrice: false,
                title: '',
                msg: '',
                msg2: '',
                price: 0,
                name: '',
                oldPrice: 0,
                isConfirm: false,
              },
            });
            dispatch({ type: SET_LOADING, payload: false });
          });
      }
    },
    [
      productFormMethods,
      errorData,
      isEdit,
      deletedOptionIds.fields,
      dispatch,
      productId,
      currentCategory?._id,
      reset,
      history,
      tabValue,
      currentBarcodeNumber,
    ]
  );

  const changeProductTab = async (_, tab) => {
    if (tab === tabValue) return;

    const result = await trigger([
      'name',
      'description',
      'price',
      'sizeTypeModifiers[0]?.list',
    ]);
    if (!result) return;

    // Read latest state after trigger. Do NOT treat empty `touchedFields` `{}`
    // as truthy — that previously always armed the discard modal when image
    // urls differed only by undefined vs ''.
    const { errors } = productFormMethods.formState;
    const hasUnsavedChanges = hasMeaningfulProductEdits({
      isDirty: productFormMethods.formState.isDirty,
      dirtyFields: productFormMethods.formState.dirtyFields,
      values: getValues(),
      savedProduct: persistedProductRef.current || formProduct || currentProduct,
      imageGetValue: getImageValue,
    });

    if (hasUnsavedChanges) {
      const confirmed = await confirm({
        hideCloseIcon: false,
        hideDiscardBtn: false,
        onClose: () => {},
      });
      if (confirmed && !Object.keys(errors).length) {
        if (tabValue === 1 && confirmed !== 'discard') {
          handleSaveModifierGroup(history.location);
          setTabValue(tab);
          dispatch({
            type: SET_PRODUCT_EVENTS_NEXT_LOCATION,
            payload: `${history.location.pathname}${history.location.search}`,
          });
        } else if (confirmed === 'discard' && !Object.keys(errors).length) {
          setTabValue(tab);
          reset(normalizeProductFormValues(formProduct) || formProduct, {
            keepDirtyValues: false,
            keepDirty: false,
          });
          setImageGetvalue('');
          if (mode === 'create') {
            history.push('/menu/overview');
          }
        } else {
          handleValidSubmit(getValues(), _).then((res) => {
            setTabValue(tab);
            reset(normalizeProductFormValues(res) || res, {
              keepDirtyValues: false,
              keepDirty: false,
            });
            setImageGetvalue('');
            dispatch({
              type: SET_PRODUCT_EVENTS_NEXT_LOCATION,
              payload: `${history.location.pathname}${history.location.search}`,
            });
          });
        }
      } else if (confirmed === 'discard') {
        setTabValue(tab);
        productFormMethods.clearErrors();
        updateFormErrors({});
        reset(normalizeProductFormValues(formProduct) || formProduct, {
          keepDirtyValues: false,
          keepDirty: false,
        });
        setImageGetvalue('');
        if (mode === 'create') {
          history.push('/menu/overview');
        }
      } else {
        setTabValue(tabValue);
        if (errorData) {
          productFormMethods.setError({ ...errorData });
          setErrorData({ ...errorData });
        }
        reset(getValues(), { keepDirty: true });
      }
    } else {
      if (productFormMethods.formState.isDirty) {
        markProductModifiersPristine();
      }
      setTabValue(tab);
    }
  };

  const changeIsOnePrice = (val = false) => {
    const initialSizeTypeModifier = [
      {
        id: uuidv4(),
        name: '',
        priceOverride: [],
        price: 0,
        pickupPrice: 0,
        optionSort: 0,
      },
      {
        id: uuidv4(),
        name: '',
        priceOverride: [],
        price: 0,
        pickupPrice: 0,
        optionSort: 1,
      },
    ];
    setValue('isOnePrice', val, { shouldValidate: true });
    if (
      !val &&
      Object.keys(productFormMethods.formState.errors).includes('priceOverride')
    ) {
      productFormMethods.clearErrors(productFormMethods.formState.name);
    }
    // const productBasePrice = getValues('price');
    const actualFormSizes = getValues()?.sizeTypeModifiers || [];
    const hasStaleFieldArrayData =
      !actualFormSizes.length && sizeTypeModifierListMethods.fields.length > 0;
    if (hasStaleFieldArrayData) {
      sizeTypeModifierListMethods.replace([]);
      sizeTypeModifierMethods.replace([]);
    }
    const effectiveListLen = hasStaleFieldArrayData
      ? 0
      : sizeTypeModifierListMethods.fields.length;
    if (!effectiveListLen || effectiveListLen === 1) {
      if (!sizeTypeModifierMethods.fields.length || hasStaleFieldArrayData) {
        sizeTypeModifierMethods.replace([
          {
            id: uuidv4(),
          },
        ]);
        let targetConnectedProviders;
        if (currentCategory) {
          targetConnectedProviders = currentCategory.connectedProviders;
        } else {
          targetConnectedProviders = menu.connectedProviders;
        }
        initialSizeTypeModifier.forEach((sizeTypeModifier) => {
          Object.keys(targetConnectedProviders).map((modItem) => {
            if (targetConnectedProviders[modItem]) {
              sizeTypeModifier.priceOverride.push({
                context_type: CONTEXT_TYPES.provider,
                context_value: modItem,
                name: modItem,
                price: null,
                pickupPrice: null,
                locked: true,
              });
            }
          });
        });
        const currentSizeTypeName = productFormMethods.getValues(
          'sizeTypeModifiers[0].name'
        );
        if (!currentSizeTypeName) {
          productFormMethods.setValue('sizeTypeModifiers[0].name', 'Size');
        }
        sizeTypeModifierListMethods.replace(initialSizeTypeModifier);
      }
    }
  };

  const handleAddModifierOption = () => {
    let connectedProviders;
    setHasDeletedItem(false);
    if (mode === 'edit') {
      connectedProviders = currentProduct?.connectedProviders;
    } else {
      connectedProviders = currentCategory?.connectedProviders;
    }

    const addSizeTypeModifier = {
      name: '',
      priceOverride: [],
      price: 0,
      pickupPrice: '',
      optionSort: sizeTypeModifierListMethods.fields.length,
    };

    Object.keys(connectedProviders).map((modItem) => {
      if (connectedProviders[modItem]) {
        addSizeTypeModifier.priceOverride.push({
          context_type: CONTEXT_TYPES.provider,
          context_value: modItem,
          name: modItem,
          price: '',
          pickupPrice: '',
          locked: true,
        });
      }
    });

    sizeTypeModifierListMethods.append(addSizeTypeModifier);
  };
  const handleDeleteModifierOption = (handleAddModifierOptionIndex, id) => {
    sizeTypeModifierListMethods.remove(handleAddModifierOptionIndex);
    setHasDeletedItem(true);
    if (id && mode === 'edit') {
      const currentDeletedOptions =
        getValues()?.sizeTypeModifiers[0]?.deletedOptionIds || [];
      const updatedDeletedOptions = [...currentDeletedOptions, id];

      setValue(
        'sizeTypeModifiers[0]',
        {
          ...getValues().sizeTypeModifiers[0],
          deletedOptionIds: updatedDeletedOptions,
        },
        { shouldDirty: true }
      );
    }
  };

  const handleSortEnd = ({ oldIndex, newIndex }) => {
    // const newList = [...sizeTypeModifierListMethods.fields];
    // newList.forEach((item, index) => {});
    // sizeTypeModifierListMethods.update(oldIndex, {
    //   ...sizeTypeModifierListMethods.fields[oldIndex],
    //   optionSort: newIndex,
    // });
    // sizeTypeModifierListMethods.update(newIndex, {
    //   ...sizeTypeModifierListMethods.fields[newIndex],
    //   optionSort: oldIndex,
    // });
    if (oldIndex !== newIndex) {
      sizeTypeModifierListMethods.move(oldIndex, newIndex);
    }
  };

  const handleSaveData = async () => {
    const confirmed = await confirm({
      hideCloseIcon: false,
      hideDiscardBtn: false,
    });
    if (!confirmed) {
      handleValidSubmit(getValues());
      // document.getElementById('productSaveButton').click();
    } else if (confirmed === 'discard') {
      reset(getValues());
    } else {
      window.location.reload();
    }
  };

  /** Only an unassign the user asked for may shrink the list we persist. */
  const handleModifierUnassigned = useCallback((modifierId) => {
    if (!modifierId) return;
    removedModifierIdsRef.current = new Set([
      ...removedModifierIdsRef.current,
      modifierId,
    ]);
  }, []);

  /**
   * Add back the assigned groups the form no longer holds — a product update
   * can reset the form without `modifiers`, and both the product PUT body and
   * the `/modifier/group` POST replace the item's list with what we send.
   */
  const withKnownAssignedModifiers = useCallback((modifiers) => {
    const list = uniqueModifiersById(modifiers || []);
    const ids = list.map((item) => item._id);
    const missing = assignedModifiersRef.current.filter(
      (item) =>
        item?._id &&
        !ids.includes(item._id) &&
        !removedModifierIdsRef.current.has(item._id)
    );
    // Recovered groups were assigned before anything the form holds, so they
    // keep the head of the list and the item's existing order.
    return missing.length ? [...missing, ...list] : list;
  }, []);

  const handleDelete = async () => {
    if (currentProduct.modifierId) {
      return handleProductIsModOption(true);
    }
    const target =
      entityType === 'item'
        ? 'item'
        : entityType === 'category'
        ? 'category'
        : 'modifier group';
    const confirmed = await confirm({
      // title: `Are you sure you want to delete the ${target}?`,
      title: `Delete ${target}`,
      message: `${t('menu.are_you_sure_delete_the')} ${target}?`,
      cancelText: t('order_view.cancel'),
      confirmText: t('table_ordering.yes_delete'),
      confirmColor: 'primary',
    });
    if (confirmed && mode == 'edit') {
      setLoading(true);
      if (entityType === 'item') {
        dispatch(deleteMenuProduct(productId))
          .then(() => {
            history.push(history.location.pathname);
            dispatch(getMenusV2()).then((res) => {
              res.categories.forEach((item) => {
                dispatch(getCategoryProductsV2(item._id)).finally(() => {
                  dispatch({ type: SET_LOADING, payload: false });
                });
              });
            });
          })
          .finally(() => {
            setLoading(false);
          });
      } else {
        dispatch(deleteModifier(modifierId))
          .then(() => {
            const indexOf = modifierGroupMethods.fields.findIndex(
              (item) => item._id === modifierId
            );
            if (indexOf != -1) {
              modifierGroupMethods.remove(indexOf);
            }
            handleModifierUnassigned(modifierId);
            if (currentCategory?._id) {
              history.push({
                pathname: history.location.pathname,
                search: `?entityType=item&pid=${productId}&cid=${currentCategory?._id}&mode=edit`,
              });
            } else {
              history.push({
                pathname: history.location.pathname,
                search: `?entityType=item&pid=${productId}&cid=${uuidv4()}&mode=edit`,
              });
            }
          })
          .finally(() => {
            setLoading(false);
          });
      }
    }
  };

  const markProductModifiersPristine = useCallback(() => {
    const values = getValues();
    const normalized = normalizeProductFormValues({
      ...values,
      modifiers: withKnownAssignedModifiers(values.modifiers),
    });
    reset(normalized, { keepDirtyValues: false, keepDirty: false });
    persistedProductRef.current = normalized;
    if (normalized?._id) {
      dispatch({ type: SET_FORM_PRODUCT, payload: normalized });
      setCurrentProduct((prev) =>
        prev ? { ...prev, modifiers: normalized.modifiers } : normalized
      );
    }
    setIsDirty(false);
    setIsChanged(false);
  }, [dispatch, getValues, reset, withKnownAssignedModifiers]);

  const handleSaveModifierGroup = (delId = '') => {
    // Prevent duplicate calls if already executing
    if (isSavingModifierGroupRef.current) {
      return;
    }

    const formModifiers = uniqueModifiersById(getValues().modifiers || []);
    const requestedModifiers = Array.isArray(delId)
      ? delId
          .filter(Boolean)
          .map(
            (id) =>
              formModifiers.find((item) => item._id === id) ||
              assignedModifiersRef.current.find((item) => item._id === id) || {
                _id: id,
              }
          )
      : formModifiers;
    const nextModifiers = withKnownAssignedModifiers(requestedModifiers);
    const nextIds = nextModifiers.map((item) => item._id);
    const currentIds = [
      ...new Set(
        (assignedModifiersRef.current.length
          ? assignedModifiersRef.current
          : formProduct?.modifiers || currentProduct?.modifiers || []
        )
          .map((item) => item._id)
          .filter(Boolean)
      ),
    ];
    const addedIds = nextIds.filter((id) => !currentIds.includes(id));
    const hasRemoved = currentIds.some((id) => !nextIds.includes(id));
    const hasDuplicateRows =
      (getValues().modifiers || []).length !== formModifiers.length;
    const hasReordered = nextIds.join(',') !== currentIds.join(',');

    if (!isEdit) {
      return;
    }

    // Membership unchanged: do not POST current ids again — that attaches
    // the same group a second time instead of replacing the list.
    if (!addedIds.length && !hasRemoved && !hasDuplicateRows && !hasReordered) {
      markProductModifiersPristine();
      return;
    }

    // Consume before any async work so a Confirm re-submit cannot leak into a
    // later modifier-only save if this call bails early next time.
    const suppressZeroPrice = suppressZeroPriceConfirmRef.current;
    suppressZeroPriceConfirmRef.current = false;

    // Mark as executing to prevent duplicate calls
    isSavingModifierGroupRef.current = true;
    setLoading(true);

    const categoryId =
      currentCategory?._id ||
      (typeof currentProduct?.category === 'object'
        ? currentProduct?.category?._id
        : currentProduct?.category) ||
      cid;

    if (hasReordered && !addedIds.length && !hasRemoved && !hasDuplicateRows) {
      Promise.resolve(
        dispatch(
          reorderModifiers(
            nextModifiers,
            categoryId,
            productId || currentProduct?._id
          )
        )
      )
        .then(() => {
          assignedModifiersRef.current = nextModifiers;
          markProductModifiersPristine();
        })
        .catch(() => {})
        .finally(() => {
          setLoading(false);
          isSavingModifierGroupRef.current = false;
        });
      return;
    }

    // POST /modifier/group replaces the item's assigned list. Always send
    // the full unique id list so assigning a new group does not drop the
    // ones already on the item. Never send the same id twice.
    groupModifier(nextIds, productId)
      .then((groupRes) => {
        dispatch(markMenuBulkUnsaved());
        // The POST just defined the assignment: it is the new source of truth.
        assignedModifiersRef.current = nextModifiers;
        removedModifierIdsRef.current = new Set();
        if (hasReordered) {
          dispatch(
            reorderModifiers(
              nextModifiers,
              categoryId,
              productId || currentProduct?._id
            )
          );
        }
        // Modifier-tab Update only runs this path (no product PUT). The API
        // returns `productZeroPrice` when assigning 0-price / optional
        // modifiers to a $0 item — surface that so the confirm modal opens.
        // Strip only on Confirm re-submit (`suppressZeroPrice`), and never
        // dispatch an empty list so we don't clear a modal the product update
        // opened in the parallel full-item save path.
        const rawEvents = groupRes?.data?.events || [];
        const events = suppressZeroPrice
          ? rawEvents.filter((event) => event?.tag !== 'productZeroPrice')
          : rawEvents;
        const hasZeroPriceConfirm = events.some(
          (event) => event?.tag === 'productZeroPrice'
        );

        if (events.length) {
          dispatch({ type: SET_PRODUCT_EVENTS, payload: events });
          localStorage.setItem('SET_MODAL_ACTIONS', ModalOpenStatusEnum.opened);
          dispatch({
            type: SET_MODAL_ACTIONS,
            payload: ModalOpenStatusEnum.opened,
          });
        }
        if (hasZeroPriceConfirm) {
          // Confirmation pending: do not refetch/overwrite the user's edits
          // (Confirm/Cancel own the next form state).
          pendingZeroPriceRef.current = true;
        }
        // Skip refetch while confirmation is pending, including when this
        // modifier save raced ahead of updateMenuProduct (armed in handleSave).
        if (pendingZeroPriceRef.current) {
          return;
        }
        const applySavedModifiers = (product) => {
          const normalized = normalizeProductFormValues({
            ...product,
            modifiers: nextModifiers,
          });
          dispatch({ type: SET_FORM_PRODUCT, payload: normalized });
          setCurrentProduct(normalized);
          return normalized;
        };
        // After a remove, do not refetch the product — GET still returns the
        // old groups and put the removed card back.
        if (hasRemoved || hasDuplicateRows) {
          const normalized = applySavedModifiers({
            ...getValues(),
            _id: productId || currentProduct?._id,
          });
          reset(normalized, { keepDirtyValues: false, keepDirty: false });
          persistedProductRef.current = normalized;
          return;
        }
        if (currentCategory?._id || cid) {
          dispatch(
            getProductById(
              productId || currentProduct?._id,
              cid || currentCategory?._id
            )
          ).then((resp) => {
            const normalized = applySavedModifiers(resp);
            // Use filtered `events` (productZeroPrice already stripped). Using
            // raw res.data.events would keep the form dirty after a successful
            // modifier save on an already-$0 product, leaving Update enabled.
            if (events.length) {
              localStorage.setItem(
                'SET_MODAL_ACTIONS',
                ModalOpenStatusEnum.opened
              );
              reset(normalized, {
                isDirty:
                  productEvents.modalStatus === ModalOpenStatusEnum.cancel ||
                  productEvents.modalStatus === ModalOpenStatusEnum.opened ||
                  events.length,
                keepDefaultValues:
                  productEvents.modalStatus === ModalOpenStatusEnum.cancel ||
                  productEvents.modalStatus === ModalOpenStatusEnum.opened ||
                  events.length,
              });
            } else {
              reset(normalized, { keepDirtyValues: false, keepDirty: false });
              persistedProductRef.current = normalized;
            }
          });
        } else {
          dispatch(getProductsAll());
        }
      })
      .catch(() => {
        // The API interceptor already reported it. Swallow here so a failed
        // save does not bubble up as an unhandled rejection.
      })
      .finally(() => {
        setLoading(false);
        isSavingModifierGroupRef.current = false; // Reset flag after completion
      });
  };

  const handleResetPriceProvider = () => {
    productPriceOverride.fields.map((item, index) => {
      productPriceOverride.update(index, {
        ...item,
        price: currentProduct.price,
        pickupPrice: currentProduct?.pickupPrice || '',
        locked: Object.prototype.hasOwnProperty.call(item || {}, 'locked')
          ? item?.locked
          : true,
      });
    });
  };

  const handleCleanPriceProvider = () => {
    productPriceOverride.fields.map((item, index) => {
      const target = currentProduct.priceOverride.find(
        (targetItem) => item.context_value === targetItem.context_value
      );
      let price;
      let pickupPrice;

      if (target?.price || target?.price === 0) {
        price = target?.price;
      } else {
        price = item?.price;
      }

      if (target?.pickupPrice || target?.pickupPrice === 0) {
        pickupPrice = target?.pickupPrice || 0;
      } else {
        pickupPrice = item?.pickupPrice || 0;
      }
      productPriceOverride.update(index, {
        ...item,
        price,
        pickupPrice,
        locked: Object.prototype.hasOwnProperty.call(item || {}, 'locked')
          ? item?.locked
          : true,
      });
      reset(formProduct);
    });
  };

  useEffect(() => {
    setTitle(getValues().name);
  }, [formState]);

  const titleModifier = (title) => {
    setCurrentModifierName(title);
  };

  const handleCloseDialog = (val) => {
    if (typeof val !== 'boolean') {
      return;
    }
    return handleProductIsModOption(val);
  };

  const connectedProvidersByTarget = useMemo(() => {
    if (mode === 'edit') {
      if (currentProduct?.modifierId) {
        return menu?.connectedProviders;
      } else {
        return formProduct?.connectedProviders;
      }
    }
    if (formProduct.modifierId) {
      return menu.connectedProviders;
    }
    if (formProduct.category) {
      return currentCategory?.connectedProviders;
    }

    return formProduct?.connectedProviders;
  }, [formProduct, menu]);

  const onChangePrice = (e, itemIndex) => {
    const { name, value } = e.target;
    const findValue = priceTransform(value);
    if (
      name.includes('priceOverride') &&
      currentCategory?.connectedProviders[providers.ubereats.srv]
    ) {
      if (findValue) {
        productFormMethods.clearErrors('price');
        productFormMethods.clearErrors('pickupPrice');
      }
    }
    if (
      name.includes('sizeTypeModifiers') &&
      currentCategory?.connectedProviders[providers.ubereats.srv]
    ) {
      if (name.includes('priceOverride') && findValue) {
        productFormMethods.clearErrors(!name.includes('priceOverride'));
      }
    }
    setValue(name, findValue, { shouldDirty: true });
  };

  const onBlurPrice = (e, itemIndex) => {
    const { name } = e.target;
    const oldPrice =
      currentProduct?.sizeTypeModifiers?.[0]?.list[itemIndex]?.price;
    const currentPrice = productFormMethods.getValues(
      `sizeTypeModifiers[0].list[${itemIndex}].price`
    );
    const oldPrice2 = currentProduct?.price;
    const currentPrice2 = productFormMethods.getValues(`price`);
    const checkProvider =
      isEdit &&
      currentProduct?.priceOverride?.find(
        (provider) => provider?.context_value !== 'main'
      );
    console.log('isPriceDirty: ', isPriceDirty);
    if (
      (((oldPrice || oldPrice === 0) && oldPrice !== currentPrice) ||
        ((oldPrice2 || oldPrice2 === 0) && oldPrice2 !== currentPrice2)) &&
      checkProvider
    ) {
      dispatch({
        type: SET_CHANGE_PRICE_V2,
        payload: {
          changeBasePrice: true,
          title: 'changeBasePriseDialog.title',
          msg: 'changeBasePriseDialog.desc',
          msg2: 'changeBasePriseDialog.desc2',
          price: name === 'price' ? currentPrice2 : currentPrice,
          oldPrice: name === 'price' ? oldPrice2 : oldPrice,
          itemIndex,
          name,
          isConfirm: false,
        },
      });
    } else {
      dispatch({
        type: SET_CHANGE_PRICE_V2,
        payload: {
          changeBasePrice: false,
          title: '',
          msg: '',
          msg2: '',
          price: name === 'price' ? oldPrice2 : oldPrice,
          isConfirm: false,
        },
      });
      // Reset isPriceDirty when no dialog is shown (no provider override) to allow form submission
      setIsPriceDirty(false);
    }
  };

  const onKeyPressPrice = (e, itemIndex) => {
    const keyCode = e.keyCode || e.which;
    if (keyCode === 13) {
      // Enter key pressed - blur the input to trigger onBlur event which calls onBlurPrice
      setIsPriceDirty(true);
      // For size items, pass itemIndex; for base price, itemIndex will be undefined
      if (itemIndex !== undefined) {
        onBlurPrice(e, itemIndex);
      } else {
        onBlurPrice(e);
      }
    }
  };

  console.log('isPriceDirty: ', isPriceDirty);

  const handleErrors = (formState, formAddOption) => {
    let haveErrors = false;
    const formErrorsCopy = { ...formErrors };
    if (!formState.name && !formAddOption) {
      haveErrors = true;
      set(formErrorsCopy, 'name', {
        error: true,
        message: 'field_is_required_',
      });
    }
    formState.list.forEach((listItem, listItemIndex) => {
      if (!listItem.name) {
        haveErrors = true;
        set(formErrorsCopy, `list[${listItemIndex}].name`, {
          error: true,
          message: 'field_is_required_',
        });
      }
      if (listItem.price === '' && listItem.price !== 0) {
        haveErrors = true;
        set(formErrorsCopy, `list[${listItemIndex}].price`, {
          error: true,
          message: 'field_is_required_',
        });
      }
      if (formState?.products) {
        formState.products.map((item) => {
          if (currentProduct?._id === item._id) {
            if (
              currentProduct?.connectedProviders[providers.ubereats.srv] &&
              listItem.price > 374 &&
              !menu?.disablePriceLimitCheck
            ) {
              haveErrors = true;
              set(formErrorsCopy, `list[${listItemIndex}].price`, {
                error: true,
                message: 'price_$375.00_for_ubereats_',
              });
            } else {
              haveErrors = false;
              set(formErrorsCopy, `list[${listItemIndex}].price`, {
                error: false,
                message: '',
              });
            }
          }
        });
      }

      listItem.priceOverride.forEach(
        (priceOverrideItem, priceOverrideItemIndex) => {
          if (
            priceOverrideItem.context_type === CONTEXT_TYPES.modifierItem &&
            priceOverrideItem.name === ''
          ) {
            haveErrors = true;
            set(
              formErrorsCopy,
              `list[${listItemIndex}].priceOverride[${priceOverrideItemIndex}].name`,
              {
                error: true,
                message: t('field_is_required_'),
              }
            );
          }
          if (priceOverrideItem.price === '' && priceOverrideItem.price !== 0) {
            haveErrors = true;
            set(
              formErrorsCopy,
              `list[${listItemIndex}].priceOverride[${priceOverrideItemIndex}].name`,
              {
                error: true,
                message: t('field_is_required_'),
              }
            );
          }
        }
      );
    });
    updateFormErrors(formErrorsCopy);
    return haveErrors;
  };

  const handleCancel = () => {
    history.push({
      pathname: history.location.pathname,
      search: '',
      state: {
        cancel: true,
      },
    });
  };

  useEffect(() => {
    // Only process when user confirms the price change (isConfirm is true)
    if (
      changeBasePriceData?.isConfirm &&
      changeBasePriceData?.price !== undefined
    ) {
      if (changeBasePriceData?.name === 'price') {
        productFormMethods.setValue(`price`, changeBasePriceData?.price);
        getValues()?.priceOverride?.forEach((_, index) => {
          productFormMethods.setValue(`priceOverride[${index}].locked`, true, {
            shouldDirty: true,
          });
        });
        // Update currentProduct immediately after confirmation to prevent showing popup again
        if (currentProduct) {
          setCurrentProduct({
            ...currentProduct,
            price: changeBasePriceData?.price,
          });
        }
      } else if (
        changeBasePriceData?.name ===
        `sizeTypeModifiers[0].list[${changeBasePriceData?.itemIndex}].price`
      ) {
        productFormMethods.setValue(
          `sizeTypeModifiers[0].list[${changeBasePriceData?.itemIndex}].price`,
          changeBasePriceData?.price
        );
        getValues().sizeTypeModifiers[0]?.list?.[
          changeBasePriceData?.itemIndex
        ]?.priceOverride?.forEach((_, provideIndex) => {
          productFormMethods.setValue(
            `sizeTypeModifiers[0].list[${changeBasePriceData?.itemIndex}].priceOverride[${provideIndex}].locked`,
            true,
            { shouldDirty: true }
          );
        });
        // Update currentProduct immediately after confirmation to prevent showing popup again
        if (
          currentProduct?.sizeTypeModifiers?.[0]?.list?.[
            changeBasePriceData?.itemIndex
          ]
        ) {
          const updatedList = [
            ...(currentProduct.sizeTypeModifiers[0].list || []),
          ];
          updatedList[changeBasePriceData?.itemIndex] = {
            ...updatedList[changeBasePriceData?.itemIndex],
            price: changeBasePriceData?.price,
          };
          setCurrentProduct({
            ...currentProduct,
            sizeTypeModifiers: [
              {
                ...currentProduct.sizeTypeModifiers[0],
                list: updatedList,
              },
            ],
          });
        }
      }
      // Reset isPriceDirty after processing confirmation to allow future updates
      setIsPriceDirty(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    changeBasePriceData?.isConfirm,
    changeBasePriceData?.price,
    changeBasePriceData?.name,
    changeBasePriceData?.itemIndex,
  ]);

  const hasUnsavedProductEdits = hasMeaningfulProductEdits({
    isDirty: formState.isDirty,
    dirtyFields: formState.dirtyFields,
    values: getValues(),
    savedProduct: persistedProductRef.current || formProduct || currentProduct,
    imageGetValue: getImageValue,
  });

  return (
    <Box sx={{ height: '100%', position: 'relative', overflow: 'hidden' }}>
      {loading && <Preloader overlay={true} />}
      <Box>
        {productEvents.eventData[0]?.tag === 'productZeroPrice' && (
          <ConfirmProductActions
            formData={() => ({
              ...getValues(),
              priceOverride: normalizePriceOverrideLocked(
                getValues()?.priceOverride
              ),
              price: 0,
              checkZeroPrice: false,
            })}
            handleSubmit={handleValidSubmit}
            setIsPriceDirty={setIsPriceDirty}
          />
        )}
        <FormHeader
          title={title}
          currentModifierName={currentModifierName}
          setCurrentModifierName={setCurrentModifierName}
          modifierFormState={modifierFormState}
          modifierMethods={modifierGroupMethods}
          // currentModifierName={currentModifierName}
          currentModifier={currentModifier}
          currentProduct={currentProduct}
          setTabValue={setTabValue}
          productId={productId}
          isDirty={isDirty}
          setIsDirty={setIsDirty}
          cid={cid}
          handleErrors={handleErrors}
          updateFormErrors={updateFormErrors}
          data={getValues()}
          formProduct={formProduct}
          entityType={entityType}
          product={currentProduct}
          category={currentCategory}
          mode={mode}
          modifier={showModifier}
          setLoading={setLoading}
          productFormMethods={productFormMethods}
          handleValidSubmit={handleValidSubmit}
          setLoadingDuplicate={setLoadingDuplicate}
          handleDelete={handleDelete}
          menu={menu}
          connectedProviders={connectedProvidersByTarget}
        />
      </Box>
      {showModifier ? (
        <ModifierGroupForm
          setTitleModifier={titleModifier}
          modifierMethods={modifierGroupMethods}
          currentModifier={currentModifier}
          currentProduct={currentProduct}
          currentCategory={currentCategory}
          setIsDirty={setIsDirty}
          isDirty={loadingDuplicate ? false : isDirty}
          setLoadingDuplicate={setLoadingDuplicate}
          setModifierFormState={setModifierFormState}
          setTabValue={(eventOrTab, tab) => {
            const nextTab = typeof eventOrTab === 'number' ? eventOrTab : tab;
            if (nextTab != null) {
              setTabValue(nextTab);
            }
          }}
          setCurrentModifier={setCurrentModifier}
          formErrors={formErrors}
          updateFormErrors={updateFormErrors}
          isChanged={isChanged}
          setIsChanged={setIsChanged}
          handleErrors={handleErrors}
          loadingDuplicate={loadingDuplicate}
        />
      ) : (
        <FormProvider
          {...productFormMethods}
          sx={{ height: '100%' }}
          component="div"
        >
          {isEdit && (
            <UpdateAvailability
              isOpen={isOpen}
              setIsOpen={setIsOpen}
              availability={getValues()}
              currentBusiness={currentBusiness}
              dispatch={dispatch}
              register={register}
              formProduct={formProduct}
              title="item"
              reset={reset}
            />
          )}

          <form
            style={{ height: '100%' }}
            onSubmit={productFormMethods.handleSubmit(
              isPriceDirty ? () => {} : handleValidSubmit,
              (err) => {
                console.log('err: ', err);
              }
            )}
          >
            <ConfirmPromptModal
              handleConfirm={productFormMethods.handleSubmit(handleValidSubmit)}
              submitData={getValues()}
              hasUnsavedChanges={menuAvailable && hasUnsavedProductEdits}
              formState={productFormMethods}
              isLoading={loading}
            />

            {/* Scrolled content */}
            <Box
              sx={{
                height: 'calc(100% - 176px)',
                overflowY: 'auto',
                pb: 3,
                pt: 4,
              }}
            >
              <Box sx={{ px: 4 }}>
                <TabsList
                  onChange={changeProductTab}
                  value={tabValue}
                  variant="scrollable"
                  scrollButtons="auto"
                >
                  <StyledTab label={t('menu.basic')} />
                  <StyledTab label={t('menu.modifiers_options')} />
                  <StyledTab label={t('menu.advanced')} />
                  <StyledTab
                    disabled={!formProduct?.modifiers?.length}
                    label={t('menu.Pre-Selected Options')}
                  />
                  {/* {checkPermission(all['SUPER']) && ( */}
                  <StyledTab label={t('menu.inventory')} />
                  {/* )} */}
                  {showPromotionTab && <StyledTab label={t('POS Promotion')} />}
                </TabsList>
              </Box>
              <TabPanel
                value={tabValue}
                index={0}
                content={
                  <Private permission="MENU_EDIT" disabled={true}>
                    <ProductFormBasic
                      imageGetvalue={setImageGetvalue}
                      isInOverview={isInOverview}
                      changeIsOnePrice={changeIsOnePrice}
                      readOnly={readOnly}
                      mode={mode}
                      onChangePrice={onChangePrice}
                      onBlurPrice={onBlurPrice}
                      connectedProvidersByTarget={connectedProvidersByTarget}
                      handleClickOpen={handleClickOpen}
                      data={getValues()}
                      formProduct={formProduct}
                      currentProductSizeTypeModifiers={
                        currentProduct?.sizeTypeModifiers
                      }
                      productPriceOverride={productPriceOverride.fields}
                      productFormMethods={productFormMethods}
                      handleAddModifierOption={handleAddModifierOption}
                      handleDeleteModifierOption={handleDeleteModifierOption}
                      sizeTypeModifierFields={sizeTypeModifierMethods.fields}
                      sizeTypeModifierListFields={
                        sizeTypeModifierListMethods.fields
                      }
                      handleSortEnd={handleSortEnd}
                      control={control}
                      handleResetPriceProvider={handleResetPriceProvider}
                      handleCleanPriceProvider={handleCleanPriceProvider}
                      currentCategory={currentCategory}
                      menu={menu}
                      currentProduct={currentProduct}
                      loading={loading}
                      initialData={initialData}
                      setDisable={setDisable}
                      onKeyPressPrice={onKeyPressPrice}
                      setIsPriceDirty={setIsPriceDirty}
                    />
                  </Private>
                }
              />
              <TabPanel
                value={tabValue}
                index={1}
                content={
                  <ModifierOptions
                    cid={cid}
                    handleSaveModifierGroup={handleSaveModifierGroup}
                    // navigateToModifierForm={navigateToModifierForm}
                    currentProduct={currentProduct}
                    currentCategory={currentCategory}
                    modifierGroupMethods={modifierGroupMethods}
                    onModifierUnassigned={handleModifierUnassigned}
                  />
                }
              />
              <TabPanel
                value={tabValue}
                index={2}
                content={
                  <Private permission="MENU_EDIT" disabled={true}>
                    <ProductFormDetails
                      setErrorData={setErrorData}
                      errorData={errorData}
                      setDisable={setDisable}
                      productFormMethods={productFormMethods}
                      reset={reset}
                      data={getValues()}
                      setValue={setValue}
                      connectedProviders={connectedProvidersByTarget}
                    />
                  </Private>
                }
              />
              <TabPanel
                value={tabValue}
                index={3}
                content={
                  <Private permission="MENU_EDIT" disabled={true}>
                    <DefaultSelections
                      productFormMethods={productFormMethods}
                    />
                  </Private>
                }
              />
              <TabPanel
                value={tabValue}
                index={4}
                style={
                  !productFormMethods.getValues('inventory')?.length
                    ? {
                        display: 'flex',
                        height: '100%',
                        justifyContent: 'center',
                        alignItems: 'center',
                      }
                    : {}
                }
                content={
                  <Private permission="MENU_EDIT" disabled={true}>
                    <>
                      <ProductFormStockInventory
                        productFormMethods={productFormMethods}
                        onAddInventory={handleGenerateInventory}
                        isGenerateInventoryLoading={generateInventoryLoading}
                      />
                      {productFormMethods.getValues('inventory')?.length >
                        0 && (
                        <>
                          <Divider sx={{ my: 2 }} />
                          <ProductFormInventory
                            productFormMethods={productFormMethods}
                          />
                        </>
                      )}
                    </>
                  </Private>
                }
              />
              {showPromotionTab && (
                <TabPanel
                  value={tabValue}
                  index={5}
                  content={
                    <Box sx={{ px: 4 }}>
                      <PromotionTab
                        lockedProductIds={[formProduct?.uuid]}
                        lockedProductName={formProduct?.name || title}
                      />
                    </Box>
                  }
                />
              )}
            </Box>
            {/* Fixed Footer */}
            {tabValue !== 4 && !(tabValue === 5 && isConnectedToPos) && (
              <Box>
                <FormFooter
                  isDisable={hasUnsavedProductEdits}
                  disabled={(disable && !hasUnsavedProductEdits) || !menuAvailable}
                  mode={mode}
                  handleSaveModifierGroup={handleSaveModifierGroup}
                  tabValue={tabValue}
                  isEdit={isEdit}
                  handleClickOpen={handleClickOpen}
                  handleCancel={handleCancel}
                />
              </Box>
            )}
          </form>
        </FormProvider>
      )}

      <Dialog
        onClose={handleCloseDialog}
        open={productIsModOption}
        PaperProps={{ sx: { p: 3 } }}
      >
        <IconButton
          sx={{ position: 'absolute', top: 0, right: 0 }}
          aria-label="close"
          onClick={() => handleCloseDialog(false)}
        >
          <CloseIcon />
        </IconButton>
        <DialogTitle sx={{ textAlign: 'center' }}>
          {t('menu.you_can_not_delete_modifier_option')}
        </DialogTitle>
        {/* <Typography sx={{ fontSize: '14px', fontWeight: 500, textAlign: 'center', color: '#3D4350', mt: 1 }}>
          You can not delete this product as it is used as a modifier option.
        </Typography> */}
      </Dialog>
      <BasePriceConfirmDialog
        open={basePriceConfirmDialogOpen}
        onClose={handleCloseBasePriceConfirmModal}
      />
      {!showModifier && (
        <ChangeBasePriseDialog setIsPriceDirty={setIsPriceDirty} />
      )}
    </Box>
  );
};

export default ProductForm;
